import { motion, AnimatePresence } from "framer-motion";
import {
  CheckCircle,
  Circle,
  Plus,
  Trash,
  Lightning,
  Brain,
  Clock,
  CalendarBlank,
  Users,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

export const TasksPanel = ({
  tasks, showNewTask, setShowNewTask, newTask, setNewTask,
  createTask, updateTaskStatus, deleteTask, analyzeTask,
  getPriorityColor, getStatusColor, agentActivity,
}) => {
  return (
    <div className="h-full flex flex-col overflow-hidden">
      <div className="border-b border-border p-6 bg-surface">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Lightning size={28} weight="duotone" className="text-foreground" />
            <div>
              <h2 className="text-2xl font-bold tracking-tight" data-testid="tasks-header-title">Task Automation</h2>
              <p className="text-sm text-muted-foreground" data-testid="tasks-header-subtitle">{tasks.length} active tasks</p>
            </div>
          </div>
          <Button onClick={() => setShowNewTask(!showNewTask)} className="bg-white text-black hover:bg-gray-200" data-testid="new-task-button">
            <Plus size={20} weight="bold" />
          </Button>
        </div>
        <AnimatePresence>
          {showNewTask && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="mt-4 p-4 border border-border bg-surface" data-testid="new-task-form">
              <form onSubmit={createTask} className="space-y-3">
                <Input value={newTask.title} onChange={(e) => setNewTask({ ...newTask, title: e.target.value })} placeholder="Task title" className="bg-transparent border-border" data-testid="new-task-title-input" />
                <Textarea value={newTask.description} onChange={(e) => setNewTask({ ...newTask, description: e.target.value })} placeholder="Task description (optional)" className="bg-transparent border-border min-h-[80px]" data-testid="new-task-description-input" />
                <div className="flex gap-2">
                  <Input type="date" value={newTask.due_date || ""} onChange={(e) => setNewTask({ ...newTask, due_date: e.target.value })} className="flex-1 bg-transparent border-border" data-testid="new-task-due-date-input" />
                  <select value={newTask.priority} onChange={(e) => setNewTask({ ...newTask, priority: e.target.value })} className="flex-1 bg-transparent border border-border p-2 text-sm" data-testid="new-task-priority-select">
                    <option value="low" className="bg-black">Low</option>
                    <option value="medium" className="bg-black">Medium</option>
                    <option value="high" className="bg-black">High</option>
                  </select>
                  <Button type="submit" className="bg-white text-black hover:bg-gray-200" data-testid="create-task-submit-button">Create</Button>
                </div>
              </form>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      <div className="flex-1 p-6 space-y-4 overflow-y-auto" data-testid="tasks-list-container">
        {tasks.length === 0 ? (
          <div className="text-center py-12" data-testid="no-tasks-message"><p className="text-muted-foreground">No tasks yet. Create one or ask the AI!</p></div>
        ) : (
          <AnimatePresence>
            {tasks.map((task, idx) => (
              <motion.div key={task.id} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ delay: idx * 0.05 }} className="border border-border bg-surface p-4 hover:-translate-y-1 transition-all duration-200" data-testid={`task-card-${task.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 flex-1">
                    <button onClick={() => updateTaskStatus(task.id, task.status === "completed" ? "pending" : "completed")} className="mt-1" data-testid={`task-status-toggle-${task.id}`}>
                      {task.status === "completed" ? (<CheckCircle size={24} weight="fill" className="text-green-500" />) : (<Circle size={24} className={getStatusColor(task.status)} />)}
                    </button>
                    <div className="flex-1">
                      <h3 className="font-semibold mb-1" data-testid={`task-title-${task.id}`}>{task.title}</h3>
                      {task.description && (<p className="text-sm text-muted-foreground mb-2" data-testid={`task-description-${task.id}`}>{task.description}</p>)}
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-xs px-2 py-1 border ${getPriorityColor(task.priority)}`} data-testid={`task-priority-${task.id}`}>{task.priority}</span>
                        {task.created_by_ai && (<span className="text-xs px-2 py-1 border border-purple-500 text-purple-500" data-testid={`task-ai-badge-${task.id}`}>AI Created</span>)}
                        {task.due_date && (<span className="text-xs px-2 py-1 border border-blue-500 text-blue-500"><CalendarBlank size={12} className="inline mr-1" />{task.due_date}</span>)}
                        {task.assigned_to && (<span className="text-xs px-2 py-1 border border-cyan-500 text-cyan-500"><Users size={12} className="inline mr-1" />{task.assigned_to}</span>)}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="ghost" onClick={() => analyzeTask(task.id)} className="hover:bg-accent" data-testid={`task-analyze-button-${task.id}`}><Brain size={18} /></Button>
                    <Button size="sm" variant="ghost" onClick={() => deleteTask(task.id)} className="hover:bg-destructive hover:text-white" data-testid={`task-delete-button-${task.id}`}><Trash size={18} /></Button>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        )}
      </div>
      <div className="border-t border-border p-6 bg-surface max-h-[250px] overflow-y-auto" data-testid="agent-activity-container">
        <div className="flex items-center gap-2 mb-4">
          <Clock size={20} className="text-muted-foreground" />
          <h3 className="font-semibold text-sm uppercase tracking-wider" data-testid="agent-activity-title">Agent Activity</h3>
        </div>
        <div className="space-y-2">
          {agentActivity.slice(0, 5).map((activity, idx) => (
            <motion.div key={activity.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: idx * 0.05 }} className="text-xs text-muted-foreground border-l-2 border-border pl-3 py-1" data-testid={`activity-item-${idx}`}>
              <div className="flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full ${getStatusColor(activity.status)}`}></span>
                <span className="font-medium" data-testid={`activity-action-${idx}`}>{activity.action}</span>
              </div>
              <p className="mt-1" data-testid={`activity-details-${idx}`}>{activity.details}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};
